<script lang="ts" setup>
import Dialog from '@jaaluu/components/Dialog.vue';
import stores from '@jaaluu/stores';
import { t } from '@shared/i18n';
import sharedStores from '@shared/shared-stores';
import { computed, ref } from 'vue';

const { favoriteGroups, activeGroupKey } = stores.favorites.state;
const { FIXED_FAV_GROUPS } = stores.favorites.consts;
const { courseStatus } = stores.course.state;
const { notify } = sharedStores.snackbar.funcs;
const { switchFavoriteGroup, deleteFavoriteGroup, createFavoriteGroup } = stores.favorites.funcs;

const props = defineProps({
  zOverlay: {
    type: Boolean,
    required: false,
    default: false
  },
  showAddButton: {
    type: Boolean,
    required: false,
    default: false
  },
  showSelect: {
    type: Boolean,
    required: false,
    default: true
  }
});

const selectItems = computed(() => {
  return Object.keys(favoriteGroups.value).filter(
    (key) => !FIXED_FAV_GROUPS.includes(key)
  );
});

const delFavGroup = (gropuName: string) => {
  deleteFavoriteGroup(gropuName);
};

const showAddDialog = ref(false);
const newGroupName = ref('');

const openAddDialog = () => {
  newGroupName.value = '';
  showAddDialog.value = true;
};

const closeAddDialog = () => {
  showAddDialog.value = false;
};

const addFavoriteGroup = () => {
  const name = newGroupName.value.trim();
  if (!name) {
    notify({
      type: 'error',
      text: t('favorites.enter_group_name') || 'Please enter a group name',
      icon: 'mdi-alert'
    });
    return;
  }
  const ok = createFavoriteGroup(name);
  if (ok) {
    showAddDialog.value = false;
    newGroupName.value = '';
  }
};
</script>

<template>
  <div :class="{ 'z-index-11': props.zOverlay }" class="d-flex" v-if="!courseStatus.isActive">
    <v-select
      v-if="props.showSelect && selectItems.length > 0"
      v-model="activeGroupKey"
      :items="selectItems.map((key) => ({ value: key, title: key }))"
      variant="underlined"
      color="primary"
      density="compact"
      class="w-fix-100"
      @update:model-value="switchFavoriteGroup"
      style="max-width: 300px"
      hide-details
      :menu-props="props.zOverlay ? { style: 'z-index: 99999 !important' } : {}"
    >
      <template v-slot:item="{ props, item }">
        <v-list-item v-bind="props" :title="item.title" density="compact">
          <template v-slot:append v-if="showAddButton">
            <v-btn
              @click="() => delFavGroup(item.title)"
              :icon="'mdi-delete'"
              variant="text"
              class="ml-2"
              size="x-small"
            />
          </template>
        </v-list-item>
      </template>
      <template v-slot:selection="{ item }">
        <div class="d-flex align-center">
          <v-icon
            :icon="
              item.value === 'default'
                ? 'mdi-folder'
                : item.value === 'course'
                  ? 'mdi-school'
                  : 'mdi-folder'
            "
            class="mr-2"
            size="x-small"
          />
          {{ item.title }}
        </div>
      </template>
    </v-select>
    <v-btn
      v-if="props.showAddButton"
      size="small"
      variant="text"
      icon="mdi-plus-circle-outline"
      @click="openAddDialog"
    ></v-btn>

    <Dialog v-model="showAddDialog" :width="300" :title="t('favorites.add_group')">
      <div>
        <v-text-field
          variant="underlined"
          v-model="newGroupName"
          label="Folder name"
          color="primary"
          @keyup.enter="addFavoriteGroup"
        />
        <div class="d-flex justify-end">
          <v-spacer />
          <v-btn size="small" color="primary" :disabled="!newGroupName" @click="addFavoriteGroup">{{
            t('add') || 'Add'
          }}</v-btn>
        </div>
      </div>
    </Dialog>
  </div>
</template>

<style scoped lang="scss">
.z-index {
  z-index: 10000 !important;
}
</style>
